// PairChoicePage — `/pair` step-2 entry.  Two action cards:
//   - Generate an invite (goes to /pair/invite)
//   - Enter an existing code (goes to /pair/accept or a small inline form)

import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import { PageHero } from "@/components/primitives/PageHero";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { acceptInvite } from "@/lib/api/pair";
import { ApiError } from "@/lib/api/pair";
import { useAuth } from "@/lib/auth/AuthProvider";
import { pairErrorMessage, PAIR_I18N } from "@/lib/i18n/pair";
import { inviteCodeSchema } from "@/lib/validation/pair";

export function PairChoicePage() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const parsed = inviteCodeSchema.safeParse({ code });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? PAIR_I18N.accept_validation);
      return;
    }
    setBusy(true);
    try {
      await acceptInvite({
        code: parsed.data.code,
        pair_name: null,
        relationship_start: null,
      });
      await refresh();
      nav("/onboarding/first-diary", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setError(pairErrorMessage(err.code, err.message));
      else setError(PAIR_I18N.invite_failed);
    } finally {
      setBusy(false);
    }
  };

  return (
    <OnboardingShell current={2}>
      <PageHero
        eyebrow="建立空間"
        title="建立你們的空間"
        subtitle="邀請你的另一半一起加入。對方會看到你的顯示名稱。"
      />

      <div className="mt-8 flex flex-col gap-4">
        <Card>
          <button
            type="button"
            onClick={() => nav("/pair/invite")}
            className="press w-full p-4 text-left hover:border-you-strong transition-colors"
            data-testid="pair-generate-card"
          >
            <div className="font-display text-title-3 text-ink-1">產生邀請碼</div>
            <p className="text-body-sm text-ink-2 mt-1">產生一組 6 位數字, 分享 QR 或連結給對方。</p>
          </button>
        </Card>

        <Card>
          <form
            onSubmit={submit}
            noValidate
            className="p-4 flex flex-col gap-3"
            data-testid="pair-accept-card"
          >
            <div className="font-display text-title-3 text-ink-1">輸入邀請碼</div>
            <p className="text-body-sm text-ink-2">對方給你的 6 位數字。</p>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              disabled={busy}
              placeholder="123456"
              data-testid="pair-code-input"
              className="h-12 text-display-3 text-center tracking-widest rounded-md border border-hairline bg-paper focus-visible:outline focus-visible:outline-2 focus-visible:outline-you-strong focus-visible:outline-offset-2"
            />
            {error ? (
              <p
                role="alert"
                className="text-body-sm text-partner-strong"
                data-testid="pair-code-error"
              >
                {error}
              </p>
            ) : null}
            <Button
              type="submit"
              variant="primary"
              disabled={busy}
              data-testid="pair-code-submit"
              leadingIcon={busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            >
              {busy ? "送出中…" : "加入"}
            </Button>
          </form>
        </Card>
      </div>
    </OnboardingShell>
  );
}
